<template>
  <div class="carbody-holographic-page nx-list-page">
    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      :row-class-name="rowClassName"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @on-load="onLoad"
    >
      <template slot="menuLeft">
        <el-button type="primary" size="small" plain icon="el-icon-download" @click="exportTrainList">导出车次汇总</el-button>
      </template>
      <template slot="exceptionStatus" slot-scope="{ row }">
        <el-tag v-if="row.unhandledProblemCount > 0" size="mini" type="danger">未整改 {{ row.unhandledProblemCount }}</el-tag>
        <el-tag v-else-if="row.problemCount > 0" size="mini" type="warning">异常 {{ row.problemCount }}</el-tag>
        <el-tag v-else size="mini" type="success">正常</el-tag>
      </template>
      <template slot="weight" slot-scope="{ row }">
        <div class="weight-cell">
          <span>毛 {{ formatWeight(row.grossWeight || row.totalWeight) }}</span>
          <span>净 {{ formatWeight(row.netWeight) }}</span>
        </div>
      </template>
      <template slot="tipplerTime" slot-scope="{ row }">
        <span>{{ row.tipplerStartTime || '-' }}</span>
        <span class="time-sep">至</span>
        <span>{{ row.tipplerEndTime || (row.tipplerStartTime ? '进行中' : '-') }}</span>
      </template>
      <template slot="tipplerDuration" slot-scope="{ row }">{{ formatDuration(row.tipplerDurationHours, row.tipplerDurationMinutes) }}</template>
      <template slot="tipplerStatusLabel" slot-scope="{ row }">
        <el-tag size="mini" :type="tipplerTagType(row.tipplerStatus)">{{ row.tipplerStatusLabel }}</el-tag>
      </template>
      <template slot="flowStatusLabel" slot-scope="{ row }">
        <el-tag size="mini" effect="plain">{{ row.flowStatusLabel }}</el-tag>
      </template>
      <template slot="inspectionStatusLabel" slot-scope="{ row }">
        <el-tag size="mini" :type="inspectionTagType(row.inspectionStatus)">{{ row.inspectionStatusLabel }}</el-tag>
      </template>
      <template slot="processStatusLabel" slot-scope="{ row }">
        <el-tag size="mini" :type="processTagType(row.processStatus)">{{ row.processStatusLabel }}</el-tag>
      </template>
      <template slot="menu" slot-scope="{ row }">
        <el-button type="text" size="small" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
        <el-button type="text" size="small" icon="el-icon-warning-outline" :disabled="!row.problemCount" @click="openProblem(row)">异常</el-button>
      </template>
    </avue-crud>

    <el-dialog
      :title="detailTitle"
      :visible.sync="detail.visible"
      width="92%"
      top="4vh"
      append-to-body
      custom-class="carbody-detail-dialog"
    >
      <div v-loading="detail.loading" class="detail-body">
        <template v-if="detail.train">
          <div class="summary-grid">
            <div class="summary-item">
              <label>车次编号</label>
              <strong>{{ detail.train.trainNumber || '-' }}</strong>
            </div>
            <div class="summary-item">
              <label>货主/煤种</label>
              <strong>{{ detail.train.custName || '-' }} / {{ detail.train.cargoName || '-' }}</strong>
            </div>
            <div class="summary-item">
              <label>车厢/重量</label>
              <strong>{{ detail.train.carriageCount || 0 }} 节 / 净 {{ formatWeight(detail.train.netWeight) }} 吨</strong>
            </div>
            <div class="summary-item">
              <label>翻车机</label>
              <strong>{{ detail.train.tipplerStatusLabel }}，{{ formatDuration(detail.train.tipplerDurationHours, detail.train.tipplerDurationMinutes) }}</strong>
            </div>
            <div class="summary-item">
              <label>业务流向</label>
              <strong>{{ detail.train.flowStatusLabel }}</strong>
            </div>
            <div class="summary-item danger" :class="{ normal: !detail.train.problemCount }">
              <label>交接异常</label>
              <strong>{{ detail.train.problemCount || 0 }} 项，未整改 {{ detail.train.unhandledProblemCount || 0 }} 项</strong>
            </div>
          </div>

          <div class="flow-strip">
            <div v-for="node in flowNodes" :key="node.key" class="flow-node" :class="{ active: node.done, danger: node.danger }">
              <i :class="node.icon"></i>
              <span>{{ node.label }}</span>
              <small>{{ node.value }}</small>
            </div>
          </div>

          <div class="detail-toolbar">
            <el-button size="small" icon="el-icon-warning-outline" :disabled="!detail.problems.length" @click="showProblemDialog(detail.problems)">查看异常项</el-button>
            <el-button size="small" icon="el-icon-finished" :disabled="!detail.problems.length" @click="showRectificationDialog(detail.problems)">查看整改结果</el-button>
            <el-button size="small" icon="el-icon-download" @click="exportDetail">导出当前车次明细</el-button>
            <el-button size="small" icon="el-icon-position" @click="jumpSource">跳转原始记录</el-button>
          </div>

          <el-tabs v-model="detail.activeTab">
            <el-tab-pane label="车厢明细" name="carriages">
              <el-table :data="detail.carriages" border stripe max-height="430" size="small">
                <el-table-column prop="carriageSerial" label="序号" width="70" align="center" />
                <el-table-column prop="carriageNumber" label="车号" min-width="110" show-overflow-tooltip />
                <el-table-column prop="carriageType" label="车型" width="90" show-overflow-tooltip />
                <el-table-column prop="grossWeight" label="毛重(吨)" width="96" align="right" :formatter="weightFormatter" />
                <el-table-column prop="standardWeight" label="标重(吨)" width="96" align="right" :formatter="weightFormatter" />
                <el-table-column prop="tareWeight" label="皮重(吨)" width="96" align="right" :formatter="weightFormatter" />
                <el-table-column prop="netWeight" label="净重(吨)" width="96" align="right" :formatter="weightFormatter" />
                <el-table-column prop="tipplerStatusLabel" label="翻车状态" width="92" />
                <el-table-column prop="passTime" label="过车时间" width="160" show-overflow-tooltip />
                <el-table-column label="异常/整改" width="116" align="center">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="mini" :disabled="!row.problemCount" @click="showProblemDialog(row.problems || [])">
                      {{ row.problemCount || 0 }} / {{ row.unhandledProblemCount || 0 }}
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </el-tab-pane>
            <el-tab-pane label="异常与整改" name="problems">
              <problem-table :problems="detail.problems" @photo="previewSingle" />
            </el-tab-pane>
          </el-tabs>
        </template>
      </div>
    </el-dialog>

    <el-dialog title="图片预览" :visible.sync="photo.visible" width="780px" append-to-body>
      <div v-if="photo.images.length" class="photo-grid">
        <el-image
          v-for="(img, index) in photo.images"
          :key="img + index"
          :src="img"
          :preview-src-list="photo.images"
          fit="cover"
        >
          <div slot="error" class="image-error">图片不可预览</div>
        </el-image>
      </div>
      <el-empty v-else description="暂无图片" />
    </el-dialog>

    <el-dialog :title="problem.title" :visible.sync="problem.visible" width="900px" append-to-body>
      <problem-table :problems="problem.rows" @photo="previewSingle" />
    </el-dialog>
  </div>
</template>

<script>
import crudMixins from '@/mixins/crud-mixins'
import { getList, getDetail } from './api'

const ProblemTable = {
  name: 'ProblemTable',
  props: {
    problems: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    emitPhoto (url) {
      this.$emit('photo', url)
    }
  },
  template: `
    <el-table :data="problems" border stripe size="small" max-height="430" empty-text="暂无异常记录">
      <el-table-column prop="trainSerial" label="序号" width="70" align="center" />
      <el-table-column prop="carriageNumber" label="车号" width="110" show-overflow-tooltip />
      <el-table-column prop="problemTypeName" label="异常项" min-width="150" show-overflow-tooltip />
      <el-table-column prop="categories" label="类别" width="110" show-overflow-tooltip />
      <el-table-column prop="exceptionLevel" label="等级" width="80" align="center" />
      <el-table-column prop="camera" label="位置" width="80" align="center" />
      <el-table-column prop="dealStatusLabel" label="整改状态" width="90" align="center">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="row.dealStatus === 1 ? 'success' : 'danger'">{{ row.dealStatusLabel }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="异常说明" min-width="140" show-overflow-tooltip />
      <el-table-column prop="handleRemark" label="整改结果" min-width="140" show-overflow-tooltip />
      <el-table-column label="图片" width="130" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" size="mini" :disabled="!row.problemPhotoPath" @click="emitPhoto(row.problemPhotoPath)">异常图</el-button>
          <el-button type="text" size="mini" :disabled="!row.handlePhotoList || !row.handlePhotoList.length" @click="emitPhoto(row.handlePhotoList[0])">整改图</el-button>
        </template>
      </el-table-column>
    </el-table>
  `
}

export default {
  name: 'CarbodyHolographicQuery',
  components: { ProblemTable },
  mixins: [crudMixins],
  data () {
    return {
      apiConfig: { getList, getDetail },
      detail: {
        visible: false,
        loading: false,
        activeTab: 'carriages',
        train: null,
        carriages: [],
        problems: []
      },
      photo: {
        visible: false,
        images: []
      },
      problem: {
        visible: false,
        title: '异常项',
        rows: []
      },
      option: {
        ...this.$store.state.publicOption,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        viewBtn: false,
        menuWidth: 160,
        column: [
          { label: '车次', prop: 'trainNumber', search: true, width: 130, fixed: true, overHidden: true, maxlength: 30 },
          { label: '车号', prop: 'carriageNumber', search: true, hide: true, maxlength: 8 },
          {
            label: '客户',
            prop: 'custNo',
            type: 'select',
            remote: true,
            dicUrl: '/api/produce/cust-info/select/distinct/pycode?custName={{key}}',
            props: {
              label: 'custName',
              value: 'custNo'
            },
            search: true,
            minWidth: 130,
            overHidden: true,
            formatter: (row) => row.custName || row.custNo || '-'
          },
          {
            label: '煤种',
            prop: 'cargoNo',
            type: 'select',
            remote: true,
            dicUrl: '/api/produce/vardict/select/distinct/pycode?varName={{key}}',
            props: {
              label: 'varName',
              value: 'varNo'
            },
            search: true,
            minWidth: 110,
            overHidden: true,
            formatter: (row) => row.cargoName || row.varName || row.cargoNo || '-'
          },
          {
            label: '流向',
            prop: 'flowStatus',
            type: 'select',
            search: true,
            hide: true,
            dicData: [
              { label: '直达客户', value: 'DIRECT' },
              { label: '入仓库', value: 'WAREHOUSE' }
            ]
          },
          {
            label: '异常',
            prop: 'exceptionStatus',
            type: 'select',
            search: true,
            width: 112,
            align: 'center',
            slot: true,
            dicData: [
              { label: '有异常', value: 'ABNORMAL' },
              { label: '未整改', value: 'UNHANDLED' },
              { label: '无异常', value: 'NORMAL' }
            ]
          },
          {
            label: '重车时间',
            prop: 'arrivalRange',
            type: 'datetimerange',
            search: true,
            hide: true,
            paramsKey: ['startTime', 'endTime'],
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            searchSpan: 8
          },
          { label: '车厢数', prop: 'carriageCount', width: 76, align: 'right' },
          { label: '重量(吨)', prop: 'weight', minWidth: 178, align: 'right', slot: true },
          { label: '重车衡过衡', prop: 'trainArrivalTime', width: 160, overHidden: true, formatter: (row) => this.formatDateTimeText(row.trainArrivalTime) },
          { label: '翻车机作业时间', prop: 'tipplerTime', minWidth: 220, overHidden: true, slot: true },
          { label: '翻车时长', prop: 'tipplerDuration', width: 100, align: 'right', slot: true },
          { label: '翻车进度', prop: 'tipplerStatusLabel', width: 96, slot: true },
          { label: '业务流向', prop: 'flowStatusLabel', width: 96, slot: true },
          { label: '交接检查', prop: 'inspectionStatusLabel', width: 96, slot: true },
          { label: '空车衡过衡', prop: 'trainDepartTime', width: 160, overHidden: true, formatter: (row) => this.formatDateTimeText(row.trainDepartTime) },
          { label: '流程状态', prop: 'processStatusLabel', width: 110, slot: true }
        ]
      }
    }
  },
  computed: {
    detailTitle () {
      return this.detail.train ? `车皮全息档案 - ${this.detail.train.trainNumber || ''}` : '车皮全息档案'
    },
    flowNodes () {
      const train = this.detail.train || {}
      return [
        { key: 'heavy', label: '重车衡过衡', icon: 'el-icon-truck', done: this.hasValidDateTime(train.trainArrivalTime), value: this.formatDateTimeText(train.trainArrivalTime) },
        { key: 'tippler', label: '翻车机作业', icon: 'el-icon-set-up', done: !!train.tipplerStartTime, value: train.tipplerStatusLabel || '-' },
        { key: 'flow', label: '煤流线/仓库', icon: 'el-icon-connection', done: train.flowStatus !== 'UNKNOWN', value: train.flowStatusLabel || '-' },
        { key: 'check', label: '交接检查', icon: 'el-icon-document-checked', done: !!train.checkTrainListId, danger: train.unhandledProblemCount > 0, value: train.inspectionStatusLabel || '-' },
        { key: 'empty', label: '空车衡过衡', icon: 'el-icon-finished', done: this.hasValidDateTime(train.trainDepartTime), value: this.formatDateTimeText(train.trainDepartTime) }
      ]
    }
  },
  methods: {
    async openDetail (row) {
      this.detail.visible = true
      this.detail.loading = true
      this.detail.activeTab = 'carriages'
      try {
        const res = await getDetail(row.id)
        const payload = (res.data && res.data.data) || {}
        this.detail.train = payload.train || row
        this.detail.carriages = payload.carriages || []
        this.detail.problems = payload.problems || []
      } finally {
        this.detail.loading = false
      }
    },
    async ensureDetail (row) {
      if (this.detail.train && String(this.detail.train.id) === String(row.id)) return
      const res = await getDetail(row.id)
      const payload = (res.data && res.data.data) || {}
      this.detail.train = payload.train || row
      this.detail.carriages = payload.carriages || []
      this.detail.problems = payload.problems || []
    },
    async openProblem (row) {
      await this.ensureDetail(row)
      this.showProblemDialog(this.detail.problems)
    },
    showProblemDialog (rows) {
      this.problem.title = '异常项'
      this.problem.rows = rows || []
      this.problem.visible = true
    },
    showRectificationDialog (rows) {
      this.problem.title = '整改结果'
      this.problem.rows = (rows || []).filter(item => item.dealStatus === 1 || (item.handlePhotoList && item.handlePhotoList.length))
      this.problem.visible = true
    },
    previewSingle (url) {
      const img = this.normalizeImageUrl(url)
      this.photo.images = img ? [img] : []
      this.photo.visible = true
    },
    normalizeImageUrl (url) {
      if (!url) return ''
      if (/^https?:\/\//.test(url) || url.indexOf('/') === 0) return url
      return `/${url}`
    },
    rowClassName ({ row }) {
      return row.unhandledProblemCount > 0 ? 'row-unhandled' : row.problemCount > 0 ? 'row-abnormal' : ''
    },
    tipplerTagType (status) {
      return status === 'DONE' ? 'success' : status === 'RUNNING' ? 'warning' : 'info'
    },
    inspectionTagType (status) {
      return status === 'CHECKED' ? 'success' : status === 'CHECKING' ? 'warning' : 'info'
    },
    processTagType (status) {
      return status === 'EXCEPTION' ? 'danger' : status === 'DONE' ? 'success' : status === 'RUNNING' ? 'warning' : 'info'
    },
    formatWeight (value) {
      if (value === null || value === undefined || value === '') return '-'
      const n = Number(value)
      return Number.isNaN(n) ? value : n.toFixed(2)
    },
    formatDuration (hours, minutes) {
      if (hours !== null && hours !== undefined && hours !== '') {
        return `${Number(hours).toFixed(2)} h`
      }
      if (minutes !== null && minutes !== undefined && minutes !== '') {
        return `${minutes} min`
      }
      return '-'
    },
    hasValidDateTime (value) {
      return !!value && String(value).indexOf('1970-01-01') !== 0
    },
    formatDateTimeText (value) {
      return this.hasValidDateTime(value) ? value : '-'
    },
    weightFormatter (row, column, value) {
      return this.formatWeight(value)
    },
    exportTrainList () {
      const th = ['车次编号', '货主', '煤种', '车厢数', '毛重', '净重', '重车衡过衡', '翻车开始', '翻车结束', '翻车时长', '业务流向', '交接检查', '异常数', '未整改数', '空车衡过衡', '流程状态']
      const rows = this.data.map(row => [
        row.trainNumber || '',
        row.custName || '',
        row.cargoName || '',
        row.carriageCount || '',
        this.formatWeight(row.grossWeight || row.totalWeight),
        this.formatWeight(row.netWeight),
        this.formatDateTimeText(row.trainArrivalTime),
        row.tipplerStartTime || '',
        row.tipplerEndTime || '',
        this.formatDuration(row.tipplerDurationHours, row.tipplerDurationMinutes),
        row.flowStatusLabel || '',
        row.inspectionStatusLabel || '',
        row.problemCount || 0,
        row.unhandledProblemCount || 0,
        this.formatDateTimeText(row.trainDepartTime),
        row.processStatusLabel || ''
      ])
      this.exportExcel(th, rows, '车皮全息档案车次汇总')
    },
    exportDetail () {
      const th = ['序号', '车号', '车型', '煤种', '毛重', '标重', '皮重', '净重', '翻车状态', '过车时间', '异常数', '未整改数']
      const rows = this.detail.carriages.map(row => [
        row.carriageSerial || '',
        row.carriageNumber || '',
        row.carriageType || '',
        row.cargoName || '',
        this.formatWeight(row.grossWeight),
        this.formatWeight(row.standardWeight),
        this.formatWeight(row.tareWeight),
        this.formatWeight(row.netWeight),
        row.tipplerStatusLabel || '',
        row.passTime || '',
        row.problemCount || 0,
        row.unhandledProblemCount || 0
      ])
      this.exportExcel(th, rows, `车皮全息档案-${this.detail.train ? this.detail.train.trainNumber : '明细'}`)
    },
    exportExcel (th, rows, filename) {
      if (!rows.length) {
        this.$message.warning('无数据可导出')
        return
      }
      const { export_json_to_excel } = require('@/util/vendor/Export2Excel')
      export_json_to_excel(th, rows, filename)
    },
    jumpSource () {
      const trainNumber = this.detail.train && this.detail.train.trainNumber
      this.$router.push({
        path: '/railway-management/transportrecord',
        query: trainNumber ? { trainNumber } : {}
      })
    }
  }
}
</script>

<style scoped lang="scss">
.carbody-holographic-page {
  padding: 0;
}

.weight-cell {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  white-space: nowrap;
}

.time-sep {
  margin: 0 4px;
  color: #909399;
}

.detail-body {
  min-height: 420px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.summary-item {
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fafafa;
  label {
    display: block;
    margin-bottom: 4px;
    color: #909399;
    font-size: 12px;
  }
  strong {
    color: #303133;
    font-size: 14px;
  }
  &.danger strong {
    color: #f56c6c;
  }
  &.normal strong {
    color: #67c23a;
  }
}

.flow-strip {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 14px;
}

.flow-node {
  display: grid;
  grid-template-columns: 24px 1fr;
  grid-template-rows: auto auto;
  gap: 2px 8px;
  align-items: center;
  padding: 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #909399;
  i {
    grid-row: 1 / 3;
    font-size: 20px;
  }
  span {
    color: #303133;
    font-weight: 600;
  }
  small {
    overflow: hidden;
    color: #909399;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &.active {
    border-color: #409eff;
  }
  &.danger {
    border-color: #f56c6c;
  }
}

.detail-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.photo-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  .el-image {
    width: 100%;
    height: 150px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background: #f5f7fa;
  }
}

.image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #909399;
}

::v-deep .row-abnormal > td {
  background: #fffaf0;
}

::v-deep .row-unhandled > td {
  background: #fff5f5;
}

@media (max-width: 1200px) {
  .summary-grid,
  .flow-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
